import React, { useState, useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@tracify/ui/dialog";
import { Button } from "@tracify/ui/button";
import { Spinner } from "@tracify/ui/spinner";
import { TrashIcon, KeyIcon } from "@heroicons/react/24/outline";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { AdConnectorAPIError } from "@/backend/types/backendTypes";
import { toast } from "sonner";
import { TaboolaCredential } from "@lib/api-hooks/useAdAccounts/types";
import axiosInstance from "@lib/axios";

type Props = {
  credentials: TaboolaCredential[];
  isPending: boolean;
};

const ManageTaboolaAccountsModal = ({ credentials, isPending }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState("");
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();
  const { selectedOrganisationData } = useSelectedStores();
  const organizationId = selectedOrganisationData?.id;

  const resetDeleteState = useCallback(() => {
    setPendingDelete(null);
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
    }
  }, []);

  const deleteMutation = useMutation({
    mutationFn: async ({
      organizationId,
      id,
    }: {
      organizationId: string;
      id: number;
    }) => {
      try {
        await axiosInstance.delete(
          `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/taboola/credentials/${organizationId}/${id}`
        );
      } catch (err) {
        if (
          err &&
          typeof err === "object" &&
          "isAxiosError" in err &&
          err.isAxiosError
        ) {
          const axiosError = err as AxiosError;
          const apiError = axiosError.response?.data as AdConnectorAPIError;
          const firstErr = apiError.detail?.[0];
          throw new Error(firstErr?.msg ?? axiosError.message);
        }
        throw new Error("Unknown error occurred!");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectedAdAccounts"] });
      setError("");
      toast.success("Successfully removed account connection");
      resetDeleteState();
    },
    onError: (error: any) => {
      setError(error.message || "Failed to delete account");
      resetDeleteState();
    },
  });

  const handleDelete = async (id: number) => {
    if (!organizationId) {
      setError("Organization ID not available");
      return;
    }

    if (pendingDelete === id) {
      try {
        await deleteMutation.mutateAsync({ organizationId, id });
        setIsOpen(false);
      } catch (error) {
        toast.error("An error occurred during deleting");
      }
    } else {
      setPendingDelete(id);
      deleteTimeoutRef.current = setTimeout(() => {
        setPendingDelete(null);
      }, 3000);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        {credentials?.length ? (
          <DialogTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsOpen(true)}
            >
              <KeyIcon className="h-4 w-4 mr-2" />
              Manage Credentials
            </Button>
          </DialogTrigger>
        ) : null}
        <DialogContent className="max-w-2xl w-full">
          <DialogHeader>
            <DialogTitle>Manage Connected Taboola Accounts</DialogTitle>
          </DialogHeader>

          {isPending ? (
            <div className="w-full flex flex-col items-center justify-center gap-4 p-4 bg-muted rounded-lg">
              <Spinner />
              <p>Loading connected accounts</p>
            </div>
          ) : credentials?.length === 0 ? (
            <div className="w-full flex flex-col items-center justify-center gap-4 p-4 bg-muted rounded-lg">
              <p>No connected Taboola accounts found.</p>
            </div>
          ) : (
            <div className="flex flex-col space-y-4">
              {error && (
                <p className="text-sm text-destructive font-medium">{error}</p>
              )}

              <div className="grid gap-4">
                {credentials?.map((credential) => (
                  <div
                    key={credential.id}
                    className="flex items-center justify-between px-4 py-2 bg-muted rounded-lg"
                  >
                    <div className="flex flex-col">
                      <p className="font-medium font-mono text-sm">
                        {credential.client_id}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {pendingDelete === credential.id && (
                        <span className="text-sm text-muted-foreground animate-pulse">
                          Click again to confirm delete
                        </span>
                      )}
                      <Button
                        variant={
                          pendingDelete === credential.id
                            ? "destructive"
                            : "ghost"
                        }
                        size="icon-sm"
                        onClick={() => handleDelete(credential.id)}
                        disabled={deleteMutation.isPending}
                      >
                        {deleteMutation.isPending ? (
                          <Spinner className="h-4 w-4" />
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageTaboolaAccountsModal;
